import { z } from 'zod';
import type { AgentRunListFilter } from '@erp/rpc';

/**
 * What the agent page's History list shows, and how that rides in the URL so
 * a link reproduces the view (`/agent-pages/<id>?skipped=show`). Pure — no
 * React, no router — so the precedence rules stay unit-testable.
 *
 * Shaped like the `agentDefinitions.listRuns` filter on purpose: later
 * filters (a date range, run categories) are new fields here, a new search
 * param, and a new field on the request, without reshaping the props that
 * carry this object down the page.
 */
export interface RunHistoryFilter {
  /** Leave out skipped checks (`skippedRunWhere` in the backend's AgentRunService). */
  hideSkipped: boolean;
}

/** Skipped checks are noise for a scheduled agent, so the list starts without them. */
export const DEFAULT_RUN_HISTORY_FILTER: RunHistoryFilter = {
  hideSkipped: true,
};

/**
 * `validateSearch` for the agent page route. A malformed value (a hand-edited
 * link) is dropped rather than failing the route.
 */
export const agentPageSearchSchema = z.object({
  skipped: z.enum(['show', 'hide']).optional().catch(undefined),
});
export type AgentPageSearch = z.infer<typeof agentPageSearchSchema>;

/**
 * The filter in effect: the URL wins (it is what a shared link means), then
 * the viewer's last choice on this browser, then the default.
 */
export function resolveRunHistoryFilter(
  search: AgentPageSearch,
  saved: RunHistoryFilter | null,
): RunHistoryFilter {
  if (search.skipped === 'show') return { hideSkipped: false };
  if (search.skipped === 'hide') return { hideSkipped: true };
  return saved ?? DEFAULT_RUN_HISTORY_FILTER;
}

/** The search params that pin `filter`, so the URL can be shared as-is. */
export function runHistorySearch(
  filter: RunHistoryFilter,
): Required<AgentPageSearch> {
  return { skipped: filter.hideSkipped ? 'hide' : 'show' };
}

/** The request filter for `agentDefinitions.listRuns`. */
export function toListRunsFilter(filter: RunHistoryFilter): AgentRunListFilter {
  return { hideSkipped: filter.hideSkipped };
}

/** A stable string per filter, to tell when the filter changed. */
export function runHistoryFilterKey(filter: RunHistoryFilter): string {
  return filter.hideSkipped ? 'hide-skipped' : 'all';
}

/**
 * How many History rows to ask for: the grown window while it still belongs
 * to the current filter, else the first page of the new list.
 */
export function historyLimitFor(
  paging: { filterKey: string; limit: number },
  currentFilterKey: string,
  pageSize: number,
): number {
  return paging.filterKey === currentFilterKey ? paging.limit : pageSize;
}

/** Browser-storage key for the viewer's last choice (one for all agent pages). */
export const RUN_HISTORY_FILTER_STORAGE_KEY = 'agent-page:run-history-filter';

const savedFilterSchema = z.object({ hideSkipped: z.boolean() });

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;

/**
 * The viewer's last choice, or null when there is none or it cannot be read
 * (private mode, blocked storage, a value from an older shape).
 */
export function readSavedRunHistoryFilter(
  storage: ReadableStorage | null,
): RunHistoryFilter | null {
  try {
    const raw = storage?.getItem(RUN_HISTORY_FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = savedFilterSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Remember the viewer's choice. Best-effort: storage may be unavailable. */
export function saveRunHistoryFilter(
  storage: WritableStorage | null,
  filter: RunHistoryFilter,
): void {
  try {
    storage?.setItem(RUN_HISTORY_FILTER_STORAGE_KEY, JSON.stringify(filter));
  } catch {
    // Quota, private mode or blocked storage: the URL still carries the view.
  }
}

/** `window.localStorage`, or null where touching it throws. */
export function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
